import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Observation } from '../../../../../../shared/models/observation';
import { ObservationsService } from '../../../../../../data/observations/observations.service';
import { NewObservation, ObservationModel } from '../../../../../../shared/models/observation.model';
import { ToastService } from '../../../../../../shared/services/toast.service';
import { ObservationsManagerService } from '../../../../../../data/observations/observations-manager.service';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatOption, MatSelect } from '@angular/material/select';
import { ObservationTypeDto } from '../../../../../../shared/models/observation-type';
import { MatInput } from '@angular/material/input';
import { BackendLocationModel } from '../../../../../../shared/models/location.model';
import { Subscription } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MapEventService } from '../../../../../../shared/components/map-viewer/services/map-event.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';


@Component({
  selector: 'app-edit-observation',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatLabel,
    FormsModule,
    MatSelect,
    MatOption,
    MatInput,
    MatTooltip,
    MatProgressSpinner,
    CdkTextareaAutosize,
  ],
  templateUrl: './edit-observation.component.html',
  styleUrl: './edit-observation.component.scss',
})
export class EditObservationComponent implements OnInit, OnDestroy {
  @Input() observation: Observation;
  @Output() observationUpdated = new EventEmitter<ObservationModel>();
  @Output() updateCanceled = new EventEmitter();

  observationTypeId: number;
  originalObservationTypeId: number;
  observationTypes: ObservationTypeDto[];
  locationUpdate: BackendLocationModel = null;
  comment: string;
  originalComment: string;

  isSaving = false;

  private locationSubscription: Subscription;

  constructor(
    private readonly observationService: ObservationsService,
    private readonly observationManager: ObservationsManagerService,
    private readonly mapEventService: MapEventService,
    private readonly toast: ToastService
  ) {}

  ngOnInit() {
    this.observationTypeId = this.observation.observationType.id;
    this.originalObservationTypeId = this.observation.observationType.id;
    this.comment = this.observation.comment;
    this.originalComment = this.observation.comment;
    this.observationTypes = this.observationManager
      .getObservationTypeGroups()
      .find(typeGroup => typeGroup.id === this.observation.observationType.observationTypeGroupId)?.observationTypes;
    this.observationManager.highlightObservation(this.observation.id, ObservationsManagerService.LIST_ACTION_SOURCE);
    this.observationManager.startEditObservation(this.observation.id);
    this.mapEventService.setObservationPlacementMode(true);
    this.locationSubscription = this.observationManager.editedObservationLocation$.asObservable().subscribe(location => {
      this.locationUpdate = location;
    });
  }

  ngOnDestroy() {
    this.locationSubscription?.unsubscribe();
    this.observationManager.stopEditingObservation(this.canSave());
    this.mapEventService.setObservationPlacementMode(false);
  }

  back() {
    this.updateCanceled.emit();
  }

  save() {
    const observation = new NewObservation();
    observation.observationTypeId = this.observationTypeId;
    observation.location = this.locationUpdate ?? this.observation.location;
    observation.comment = this.comment?.substring(0, 256);
    const observationType = this.observationTypes?.find(type => type.id === this.observationTypeId);
    observation.description = observationType?.title ?? '';
    observation.timestamp = null;

    this.isSaving = true;
    this.observationService
      .updateObservation(this.observation.id, observation)
      .then(response => {
        this.toast.long('Observation updated.');
        this.locationUpdate = null;
        this.originalObservationTypeId = response.typeId;
        this.originalComment = response.comment;
        this.observationUpdated.emit(response);
        this.isSaving = false;
      })
      .catch(error => {
        this.isSaving = false;
        this.toast.long('Observation could not be updated');
      });
  }

  typeChanged(observationTypeId: number) {
    this.observationManager.updateObservationType(observationTypeId);
  }

  canSave(): boolean {
    // type changed or location changed or comment changed
    return this.observationTypeId !== this.originalObservationTypeId ||
      !!this.locationUpdate ||
      this.comment !== this.originalComment;
  }
}
